<template>
  <div class="app-root">
    <!-- Redesigned sticky navigation -->
    <header v-if="showHeader" class="ds-nav">
      <!-- Logo -->
      <router-link to="/home" class="logo-link">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z" fill="#4F8646"/>
        </svg>
        <span class="logo-text">Meal Mate</span>
      </router-link>

      <!-- Desktop Nav links -->
      <div class="nav-links hidden lg:flex" data-tour="nav">
        <router-link
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          class="ds-nav-link"
          :class="{ active: isActive(item.path) }"
        >
          {{ item.label }}
        </router-link>
      </div>

      <!-- Right: user info + mobile menu -->
      <div class="nav-right">
        <!-- Group selector / badge (desktop) -->
        <div v-if="isAuthenticated && currentGroup && userGroups" class="hidden lg:flex items-center gap-2">
          <select
            v-if="userGroups.length > 1"
            :value="currentGroup.active_group.id"
            @change="handleGroupChange(Number(($event.target as HTMLSelectElement).value))"
            class="nav-group-select"
          >
            <option v-for="group in userGroups" :key="group.id" :value="group.id">
              {{ group.name }}
            </option>
          </select>
          <span v-else class="group-badge">
            {{ currentGroup.active_group.name }}
          </span>
        </div>

        <!-- Help & getting started (desktop) -->
        <router-link v-if="isAuthenticated" to="/help" class="help-link hidden lg:flex" title="Help & getting started"
          aria-label="Help & getting started">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </router-link>

        <!-- Notification bell (desktop) -->
        <NotificationBell v-if="isAuthenticated" class="hidden lg:flex" />

        <!-- User avatar + name → links to profile (desktop) -->
        <router-link v-if="isAuthenticated && currentUser" to="/user" class="user-link hidden lg:flex">
          <div class="user-avatar">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="#4F8646">
              <path d="M10 10a4 4 0 100-8 4 4 0 000 8zm0 2c-4 0-7 2-7 3v1h14v-1c0-1-3-3-7-3z"/>
            </svg>
          </div>
          <span class="user-name">{{ currentUser.first_name }}</span>
        </router-link>
        <button v-if="isAuthenticated" @click="handleLogout" class="ds-nav-link logout-btn hidden lg:flex">Logout</button>

        <!-- Mobile Header (hamburger) -->
        <MobileHeader
          :is-authenticated="isAuthenticated"
          :current-user="currentUser"
          :current-group="currentGroup"
          :user-groups="userGroups"
          @logout="handleLogout"
          @group-change="handleGroupChange"
        />
      </div>
    </header>

    <!-- Main content -->
    <main :class="showHeader ? '' : ''">
      <router-view />
    </main>

    <!-- Global Custom Alert -->
    <CustomAlert :show="alertState.show" :type="alertState.type" :title="alertState.title" :message="alertState.message"
      :confirm-text="alertState.confirmText" :cancel-text="alertState.cancelText" :show-cancel="alertState.showCancel"
      @confirm="handleAlertConfirm" @cancel="handleAlertCancel" @close="handleAlertClose" />

    <!-- Global Toast Notifications -->
    <ToastContainer />

    <!-- Guided onboarding coachmark tour (new users) -->
    <OnboardingTour />

    <!-- Getting-started checklist (coexists with the tour) -->
    <OnboardingChecklist v-if="isAuthenticated" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from './stores/auth'
import { useNotificationStore } from './stores/notifications'
import { useOnboardingStore } from './stores/onboarding'
import CustomAlert from './components/CustomAlert.vue'
import ToastContainer from './components/common/ToastContainer.vue'
import MobileHeader from './components/layout/MobileHeader.vue'
import NotificationBell from './components/notifications/NotificationBell.vue'
import OnboardingTour from './components/onboarding/OnboardingTour.vue'
import OnboardingChecklist from './components/onboarding/OnboardingChecklist.vue'
import { alertState, closeAlert } from './utils/errorHandling'

const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const onboardingStore = useOnboardingStore()

// Start the guided tour once for a newly authenticated user (no-op if already seen).
// Requires preferences to be loaded — that's where onboarding_done/step live.
const maybeStartOnboarding = () => {
  if (!authStore.isAuthenticated || !authStore.preferences) return
  onboardingStore.maybeAutoStart()
}
const router = useRouter()
const route = useRoute()

const navItems = [
  { label: 'Dashboard', path: '/home' },
  { label: 'Meals', path: '/meals' },
  { label: 'Recipes', path: '/recipes' },
  { label: 'Shopping Lists', path: '/shopping-lists' },
]

const isActive = (path: string) => {
  if (path === '/home') return route.path === '/home'
  return route.path.startsWith(path)
}

// Initialize authentication on app start, then start notification polling
onMounted(async () => {
  await authStore.initializeAuth()
  if (authStore.isAuthenticated) {
    notificationStore.startPolling()
    maybeStartOnboarding()
  }
})

// Handle login/logout events after mount (e.g. navigation from login page)
watch(
  () => authStore.isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      notificationStore.startPolling()
      maybeStartOnboarding()
    } else {
      notificationStore.stopPolling()
    }
  }
)

// Preferences load asynchronously after auth; start onboarding once they arrive.
watch(
  () => authStore.preferences,
  () => maybeStartOnboarding()
)

// Computed properties
const isAuthenticated = computed(() => authStore.isAuthenticated)
const currentUser = computed(() => authStore.currentUser)
const currentGroup = computed(() => authStore.activeGroup)
const userGroups = computed(() => authStore.groups)
const showHeader = computed(() => {
  // Hide header on authentication pages and password reset pages
  const authPaths = ['/login', '/register', '/forgot-password', '/reset-password']
  return !authPaths.some(path => route.path.startsWith(path))
})

// Alert event handlers
const handleAlertConfirm = () => {
  closeAlert()
}

const handleAlertCancel = () => {
  closeAlert()
}

const handleAlertClose = () => {
  closeAlert()
}

// Methods
const handleLogout = async () => {
  try {
    await authStore.logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout failed:', error)
    // Even if logout API fails, redirect to login
    router.push('/login')
  }
}

const handleGroupChange = async (groupId: number) => {
  try {
    await authStore.setUserActiveGroup(groupId)
  } catch (error) {
    console.error('Failed to change active group:', error)
  }
}
</script>

<style scoped>
.app-root {
  min-height: 100vh;
  background: var(--bg-app);
}

.logo-link {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  cursor: pointer;
}

.logo-text {
  font-size: 16px;
  font-weight: 700;
  color: #0F172A;
  letter-spacing: -0.3px;
  font-family: 'Inter', sans-serif;
}

.nav-links {
  gap: 4px;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.group-badge {
  font-size: 12px;
  font-weight: 600;
  color: #64748B;
  background: #F1F5F9;
  padding: 3px 10px;
  border-radius: 9999px;
}

.nav-group-select {
  height: 30px;
  border-radius: 20px;
  border: 1px solid #E2E8F0;
  background-color: #F1F5F9;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%2394A3B8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round' viewBox='0 0 24 24'%3E%3Cpath d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding: 0 30px 0 12px;
  font-size: 12px;
  font-family: Inter, sans-serif;
  font-weight: 600;
  color: #64748B;
  outline: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  transition: border-color 0.15s;
}
.nav-group-select:hover { border-color: #CBD5E1; }
.nav-group-select:focus { border-color: #4F8646; }

.help-link {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: background 0.15s, color 0.15s;
}
.help-link:hover {
  background: #f1f5f9;
  color: #4f8646;
}

.user-link {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #EDF3EC;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-name {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.logout-btn {
  color: #BA1A1A;
  font-size: 12px;
  padding: 4px 10px;
}
</style>
